import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="group-chat-form"
export default class extends Controller {
  static targets = ["searchInput", "searchType", "results", "selectedContainer", "participantIds"]
  static values = { searchUrl: String }

  connect() {
    this.selectedParticipants = new Map() // Map of "type-id" -> {id, type, name, initials}
    this.searchTimeout = null
  }

  switchTab(event) {
    const searchType = event.params.type
    this.searchTypeTarget.value = searchType

    // Update placeholder
    this.searchInputTarget.placeholder = `Search ${searchType === 'staff' ? 'staff' : 'guardians'}...`

    // Clear search input
    this.searchInputTarget.value = ""
    this.searchInputTarget.focus()

    // Update tab styling
    const buttons = this.element.querySelectorAll('button[data-action*="switchTab"]')
    buttons.forEach(btn => {
      const isActive = btn.dataset.groupChatFormTypeParam === searchType
      if (isActive) {
        btn.style.borderColor = '#0d9488'
        btn.style.color = '#0d9488'
        btn.style.background = 'rgba(20, 184, 166, 0.05)'
        btn.classList.remove('chat-text-secondary')
      } else {
        btn.style.borderColor = 'transparent'
        btn.style.color = ''
        btn.style.background = 'transparent'
        btn.classList.add('chat-text-secondary')
      }
    })

    // Load initial list for the new tab
    this.performSearch("")
  }

  search() {
    clearTimeout(this.searchTimeout)

    const query = this.searchInputTarget.value.trim()
    if (query.length === 0) {
      // Load initial list when search is cleared
      this.performSearch("")
      return
    }

    if (query.length < 2) {
      return
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    const searchType = this.searchTypeTarget.value
    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set('query', query)
    url.searchParams.set('search_type', searchType)
    url.searchParams.set('context', 'group')

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error('Search failed:', error)
    }
  }

  selectParticipant(event) {
    const { id, type, name, initials } = event.params
    const key = `${type}-${id}`

    if (this.selectedParticipants.has(key)) {
      return // Already selected
    }

    this.selectedParticipants.set(key, { id, type, name, initials })
    this.renderSelectedParticipants()
    this.updateHiddenFields()

    // Hide the selected item from results
    const resultItem = event.currentTarget
    resultItem.classList.add('hidden')
  }

  removeParticipant(event) {
    const { id, type } = event.params
    const key = `${type}-${id}`

    this.selectedParticipants.delete(key)
    this.renderSelectedParticipants()
    this.updateHiddenFields()

    // Show the item in results again if still visible
    const resultItem = this.resultsTarget.querySelector(`[data-participant-key="${key}"]`)
    if (resultItem) {
      resultItem.classList.remove('hidden')
    }
  }

  renderSelectedParticipants() {
    if (this.selectedParticipants.size === 0) {
      this.selectedContainerTarget.innerHTML = ''
      return
    }

    const chips = Array.from(this.selectedParticipants.entries()).map(([key, participant]) => {
      const bgColor = participant.type === 'User' ? 'bg-emerald-100 text-emerald-800' : 'bg-purple-100 text-purple-800'
      return `
        <span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${bgColor}">
          <span class="w-5 h-5 rounded-full ${participant.type === 'User' ? 'bg-emerald-500' : 'bg-purple-500'} text-white text-xs flex items-center justify-center">
            ${participant.initials}
          </span>
          ${participant.name}
          <button type="button"
                  class="ml-1 hover:opacity-70"
                  data-action="click->group-chat-form#removeParticipant"
                  data-group-chat-form-id-param="${participant.id}"
                  data-group-chat-form-type-param="${participant.type}">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </span>
      `
    }).join('')

    this.selectedContainerTarget.innerHTML = chips
  }

  updateHiddenFields() {
    // Clear existing hidden fields
    this.participantIdsTarget.innerHTML = ''

    // Group by type
    const userIds = []
    const guardianIds = []

    this.selectedParticipants.forEach((participant, key) => {
      if (participant.type === 'User') {
        userIds.push(participant.id)
      } else {
        guardianIds.push(participant.id)
      }
    })

    // Add hidden fields for user IDs
    userIds.forEach(id => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'chat[user_participant_ids][]'
      input.value = id
      this.participantIdsTarget.appendChild(input)
    })

    // Add hidden fields for guardian IDs
    guardianIds.forEach(id => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'chat[guardian_participant_ids][]'
      input.value = id
      this.participantIdsTarget.appendChild(input)
    })
  }
}
